/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useLocation, useParams } from 'react-router-dom';
import useBreakpoints from '../../../../../../../common/hooks/useBreakpoints';
import { GridPager } from '../../../../../../../common/widgets/GridPager';
import Icons from '../../../../../../../common/widgets/Icons';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../../config';
import {
  DiscoverCardTypes,
  RevolutionSliderTypes,
  SortingOrderTypes,
} from '../../../../../enums';
import { parseProducts } from '../../../../../helpers';
import { CategoryDetails } from '../../../../../models/Categories';
import {
  ProductCardModel,
  ProductListModel,
} from '../../../../../models/Product';
import { resources } from '../../../../../resources/resources';
import BenefitBucksWallet from '../../../../CategoryPage/Components/BenefitBucks';
import { FilterPanelModel } from '../../../../CategoryPage/types';
import {
  getSortedProductCards,
  updatePagingQueryParam,
} from '../../../../CategoryPage/utils';
import MktSlideshow from '../../../../Common/Marketing/MktSlideshow';
import ProductListItem from '../../../../Common/ProductListItem';
import FilterDialog from './dialogs/FilterDialog';
import SortDialog from './dialogs/SortDialog';

type PageContent = {
  total: number;
  items: ProductCardModel[] | undefined;
};

interface IMobileProductsProps {
  filteredProducts?: ProductListModel[];
  searchValue?: string;
  handleSortingOrderChange: (id: number) => void;
  filters: FilterPanelModel;
  isTabletView: boolean;
  isDiscountVoucherList: boolean;
  isBenefitBucksList: boolean;
  title?: string;
  categoryDetails?: CategoryDetails;
  showBrands?: boolean;
  initialSortId?: number;
  initialSkip?: number;
  initialTake?: number;
  forcedCategoryId?: string;
}

const MobileProductsComponent = (props: IMobileProductsProps) => {
  const { isOnlyTablet } = useBreakpoints();
  const location = useLocation();
  const params = useParams<{ categoryId?: string; searchTerm?: string }>();
  const categoryId = props.forcedCategoryId || params.categoryId;
  const htmlElement = document.getElementsByTagName('html')[0];
  const { t: translate } = useTranslation();
  const {
    filteredProducts,
    searchValue,
    handleSortingOrderChange,
    filters,
    isDiscountVoucherList,
    isBenefitBucksList,
    title: pageTitle,
    categoryDetails,
    showBrands,
    initialSortId,
    initialSkip,
    initialTake,
  } = props;

  const [showSortDialog, setShowSortDialog] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<number>(
    initialSortId ?? SortingOrderTypes.TheMostPopular,
  );
  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [sortedProductList, setSortedProductList] = useState<
    ProductCardModel[] | undefined
  >([]);
  const [pageContent, setPageContent] = useState<PageContent>({
    total: 0,
    items: undefined,
  });
  const [page, setPage] = useState({
    skip: initialSkip ?? 0,
    take: initialTake ?? config.listPageSize,
  });

  const hasFilters =
    !!filters.discountedProductsCount ||
    (filters.filteredAvailableInList &&
      filters.filteredAvailableInList?.length > 1) ||
    (filters.filteredBrandList && filters.filteredBrandList.length > 1) ||
    new Set(filters.filteredTaxationLevelList?.map(it => it.taxClassId)).size >
      1 ||
    filters.priceRange?.min !== filters.priceRange?.max;

  const handlePageChange = (e: any) => {
    updatePagingQueryParam(e.skip, e.take);
    setPage({
      skip: e.skip,
      take: e.take,
    });
  };

  useEffect(() => {
    if (showFilterDialog) {
      htmlElement.classList.add('dialog-active');
    } else {
      htmlElement.classList.remove('dialog-active');
    }

    return () => {
      htmlElement.classList.remove('dialog-active');
    };
  }, [showFilterDialog, props.isTabletView]);

  useEffect(() => {
    const sorted = getSortedProductCards[sortOrder](sortedProductList);

    if (!_.isEqual(sorted, sortedProductList)) {
      setSortedProductList(sorted);
    }
  }, [sortedProductList]);

  useEffect(() => {
    if (filteredProducts) {
      const products = parseProducts(
        [...filteredProducts],
        showBrands && Number(categoryId) !== DiscoverCardTypes.Cashback,
      );
      if (products && products.length <= page.skip) {
        updatePagingQueryParam(0, page.take);
        setPage({
          skip: 0,
          take: page.take,
        });
      }
      setSortedProductList(products);
    }
  }, [filteredProducts]);

  useEffect(() => {
    if (sortedProductList) {
      const items = sortedProductList.slice(page.skip, page.skip + page.take);
      setPageContent({
        total: sortedProductList.length,
        items,
      });
    }
  }, [page.skip, page.take, sortedProductList]);

  return (
    <section className="section-main section-full mobile-page category-page">
      <div className="container">
        <div className="product-list-header-mb">
          {location.pathname.includes('search') ? (
            <Loader loading={!filteredProducts} loader={<Skeleton />}>
              <span className="search-title">{`${filteredProducts?.length} ${translate(
                resources.searchPage.mobile.result,
              )} "${searchValue ?? params.searchTerm}"`}</span>
            </Loader>
          ) : (
            <>
              {isOnlyTablet && (
                <MktSlideshow
                  sliderType={RevolutionSliderTypes.HomeSlideshow}
                />
              )}
              <h1 className="category-title">{pageTitle ?? ''}</h1>
            </>
          )}
          {categoryDetails?.showBenefitBucksWallet && <BenefitBucksWallet />}
          <div className="mobile-filters-area">
            {filteredProducts && filteredProducts.length > 2 && (
              <button className="btn" onClick={() => setShowSortDialog(true)}>
                {translate(resources.searchPage.sort)}
                <span className="btn-icon">
                  <Icons.ArrowDownMobileFilter />
                </span>
              </button>
            )}
            {hasFilters && (
              <button className="btn" onClick={() => setShowFilterDialog(true)}>
                {translate(resources.searchPage.filter)}
                <span className="btn-icon">
                  <Icons.ArrowDownMobileFilter />
                </span>
              </button>
            )}
          </div>
          {showSortDialog && (
            <SortDialog
              closeDialog={() => setShowSortDialog(false)}
              handleSortingOrderChange={handleSortingOrderChange}
              order={sortOrder}
              isDiscountVoucherList={isDiscountVoucherList}
              isBenefitBucksList={isBenefitBucksList}
              isSearchResults={true}
              setOrder={setSortOrder}
            />
          )}
          {showFilterDialog && hasFilters && (
            <FilterDialog
              closeDialog={() => setShowFilterDialog(false)}
              filters={filters}
              filteredProductList={filteredProducts}
            />
          )}
        </div>
        <div className="area-content products-content-mb">
          <Loader loading={!filteredProducts} loader={<LoadingSpinner />}>
            <div className="card-list card-list-4 card-list-sm-3 card-list-xs-2">
              {!!pageContent.items &&
                _.map(pageContent.items, (product: ProductCardModel) => {
                  return (
                    <ProductListItem
                      key={product.productId ?? product.brandId}
                      product={product}
                    />
                  );
                })}
            </div>
            <div className="minimal-pagination mobile-pagination">
              <GridPager
                total={pageContent.total}
                skip={page.skip}
                take={page.take}
                buttonCount={5}
                previousNext
                size="small"
                info={false}
                onPageChange={handlePageChange}
              />
            </div>
          </Loader>
          {categoryDetails && categoryDetails.description.length > 0 && (
            <div
              id="category-description"
              className="mobile-category-description"
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: categoryDetails?.description || '<></>',
                }}
              ></div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MobileProductsComponent;
